import { useState } from 'react';
import { organizationsApi } from '../../../../../lib/organizations';
import { extractApiError } from '../../../../../lib/api';
import type { ApprovalLineItem } from '@safetyhub/report-composer';
import { defaultNotifyVia } from '../notifyChannelOptions';

/** 외부 결재자(회사 멤버) 등록 폼 */
export interface CreateMemberForm {
  name: string;
  email: string;
  phone: string;
}

/**
 * 결재라인 편집 모달의 **결재자 Member 확보** 폼 상태 — 두 갈래를 한 폼이 맡는다:
 *  ① 신규 등록(`createExternalMember`) · ② 기존 인원의 연락처 보정(`updateMemberContact`).
 * 어느 쪽이든 끝은 같다 — 그 Member를 결재라인 칸에 `memberId`로 지정한다.
 *
 * 신원 축은 Member 하나다(별도 결재자 프로필 축을 두지 않는다).
 */
export function useApproverMemberForm(
  companyId: string,
  editingLines: ApprovalLineItem[],
  setEditingLines: (lines: ApprovalLineItem[]) => void,
  onMembersRefresh?: () => Promise<void>,
) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<CreateMemberForm>({
    name: '',
    email: '',
    phone: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  /**
   * F-G: **연락처 보정 모드**. 값이 있으면 새로 만들지 않고 그 멤버의 연락처를 채운 뒤
   * 곧바로 결재라인에 지정한다(칸 index를 함께 들고 있으면 그 칸에, 없으면 끝에 추가).
   *
   * 이 모드가 필요한 이유: F-F가 "계정도 연락처도 없는 사람"의 지정을 막는데, 막기만 하고
   * 채울 길이 없으면 **막다른 길**이 된다. 선택하는 순간 폼이 열려야 조치로 이어진다.
   */
  const [fixingMember, setFixingMember] = useState<{ id: string; name: string; index?: number } | null>(null);

  const openCreateModal = () => {
    setFixingMember(null);
    setForm({ name: '', email: '', phone: '' });
    setError('');
    setShowModal(true);
  };

  /** 연락처가 없는 기존 인원을 고르면 이 폼이 열린다(이름은 고정, 연락처만 받는다). */
  const openContactFixModal = (member: { id: string; name: string }, index?: number) => {
    setFixingMember({ ...member, index });
    setForm({ name: member.name, email: '', phone: '' });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // 폼 유효성(전화번호=신원 키 필수)은 ApproverContactFormModal이, 번호 형식·정규화는 `lib/phone.ts`가
  // 소유한다 — 등록·보정 두 경로가 같은 규칙을 쓰도록 한 벌만 둔다.

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
      };

      // F-G: 보정 모드면 기존 인원의 연락처를 채운다(새 인원을 만들지 않는다 — 같은 사람이
      // 둘로 갈라지면 결재 이력이 나뉜다). 아니면 기존대로 회사 멤버로 등록.
      const newMember = fixingMember
        ? await organizationsApi.updateMemberContact(companyId, fixingMember.id, {
            phone: payload.phone!,
            email: payload.email,
          })
        : await organizationsApi.createExternalMember(companyId, payload);
      if (onMembersRefresh) await onMembersRefresh();
      setShowModal(false);

      const memberDisplay = {
        id: newMember.id,
        name: newMember.name,
        phone: newMember.phone ?? null,
        email: newMember.email ?? null,
      };

      // 보정 모드에서 칸 index가 주어졌으면 **그 칸에** 지정한다(칸을 새로 만들지 않는다) —
      // 미지정 칸은 뒤쪽에 모이므로 중간 칸을 채우려면 자리를 지정해 넣어야 한다.
      if (fixingMember?.index !== undefined) {
        const target = fixingMember.index;
        setEditingLines(
          editingLines.map((line, i) =>
            i === target
              ? {
                  ...line,
                  memberId: memberDisplay.id,
                  member: memberDisplay,
                  sourceType: 'member' as const,
                  // 담당자가 바뀌었으므로 채널 선택을 새 담당자의 보유 채널로 재시드
                  // (정본 축 notifyVia 하나 — 2026-08-23 단일화)
                  notifyVia: defaultNotifyVia(memberDisplay),
                  _assigneeUnset: false,
                }
              : line,
          ),
        );
        setFixingMember(null);
        return;
      }

      const newIndex = editingLines.length;
      const newLevel = newIndex + 1;
      setEditingLines([
        ...editingLines,
        {
          memberId: memberDisplay.id,
          member: memberDisplay,
          approvalLevel: newLevel,
          label: `${newLevel}단계`,
          // 출처 축 제거(2026-08-23): 종류 축만 시드
          notifyVia: defaultNotifyVia(memberDisplay),
        },
      ]);
      setFixingMember(null);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    showModal,
    form,
    isSaving,
    error,
    openCreateModal,
    openContactFixModal,
    /** 연락처 보정 모드인가 — 폼 문구를 가르는 데 쓴다 */
    isFixingContact: fixingMember !== null,
    closeModal,
    handleSave,
    onFormChange: (updates: Partial<CreateMemberForm>) => setForm({ ...form, ...updates }),
  };
}
